import React, { useEffect, useState } from "react";
import { Button, Modal, Form, FormGroup, Input, Label } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { ErrorHandler, useErrors } from "services/customHooks/useErrors";
import { KeyValuePair } from "services/types/KeyValuePair";
import { AppContext } from "services/appContext/AppContext";
import TextFormInput from "components/framework/forms/TextFormInput";
import DropdownFormInput from "components/framework/forms/DropdownFormInput";
import { CarrierIntegrationApi } from "services/apis/CarrierIntegrationApi";
import { useIsMounted } from "services/customHooks/useIsMounted";
import {
  CarrierIntegrationDto,
  CarrierIntegrationGetDto,
  CarrierIntegrationListDto,
  CreateIntegrationMappingDto
} from "services/apis/types/carrierIntegration/CarrierIntegrationDto";
import DropdownInput from "components/framework/forms/DropdownInput";
import { CustomCarrierIntegrationDropdown } from "./CustomCarrierIntegrationDropdown";
import { showInfoNotification } from "components/framework/notification/NotificationUtil";
import { handleError } from "services/util/ApiUtil";
import { AddError, Errors, HasErrors } from "components/framework/errorHandling/ErrorUtil";
import { nameOf } from "services/util/ObjectUtil";
import { LoadingIndicator } from "components/framework/loadingIndicator/LoadingIndicator";

type Props = {
  isEditMode?: boolean;
  initialData?: any;
  onClose: () => void;
  onSuccess: () => void;
};

interface FormData {
  spid: string;
  losingCarrier: number | null;
  losingCarrierSpid: string;
  carrierAccountId: string;
  integrationEnabled: boolean;
}

export default function AddCarrierIntegrationModal(props: Props) {
  const intl = useIntl();
  const { appContext } = React.useContext(AppContext);
  const theme = appContext.theme;
  const isMounted = useIsMounted();
  const [formData, setFormData] = useState<FormData>({
    spid: "",
    losingCarrier: null,
    losingCarrierSpid: "",
    carrierAccountId: "",
    integrationEnabled: false
  });

  const [carrierOptions, setCarrierOptions] = useState<CarrierIntegrationDto[]>([]);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setErrors, getErrorHandler } = useErrors();
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

  useEffect(() => {
    if (!props.isEditMode || !props.initialData?.carrierIntegrationSettingId) {
      CarrierIntegrationApi.getCarrierIntegrationList().then(
        (result: CarrierIntegrationDto[]) => {
          if (isMounted.current) {
            setCarrierOptions(result);
          }
        }
      );
      return;
    }

    setShowLoadingIndicator(true);

    Promise.all([
      CarrierIntegrationApi.getCarrierIntegrationList(),
      CarrierIntegrationApi.getCarrierIntegration(
        props.initialData.carrierIntegrationSettingId
      )
    ])
      .then(([carrierList, integrationResponse]) => {
        if (!isMounted.current) return;

        setCarrierOptions(carrierList);

        const data = integrationResponse[0];

        const selectedCarrier = carrierList.find(
          (item) => item.name === data.losingCarrier
        );

        setFormData({
          spid: Array.isArray(data.spids)
            ? data.spids.join(",")
            : data.spids || "",
          losingCarrier: selectedCarrier?.carrierIntegrationsId ?? null,
          losingCarrierSpid: data.losingCarrierSpid || "",
          carrierAccountId: data.carrierAccountId || "",
          integrationEnabled: data.integrationEnabled || false
        });
      })
      .catch((error) => handleError(error))
      .finally(() => setShowLoadingIndicator(false));
  }, [props.isEditMode, props.initialData]);

  const handleInputChange = (key: keyof typeof formData, value: string | boolean | number) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    setIsLoading(true);
    const spidArray = formData.spid.toUpperCase()
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const payload: CreateIntegrationMappingDto = {
      spid: spidArray,
      losingCarrier: Number(formData.losingCarrier),
      losingCarrierSpid: formData.losingCarrierSpid.toUpperCase(),
      carrierAccountId: formData.carrierAccountId,
      integrationEnabled: formData.integrationEnabled
    };
    const errors = validateModel(payload);
    if (HasErrors(errors)) {
      setErrors(errors);
      if(isMounted.current) {
        setIsLoading(false);
      }
    } else {
      const apiCall = props.isEditMode
        ? CarrierIntegrationApi.update(payload, props.initialData.carrierIntegrationSettingId)
        : CarrierIntegrationApi.create(payload);
      apiCall
        .then(() => {
            showInfoNotification(
              intl.formatMessage(props.isEditMode ? { id: "carrierIntegration.update.success" }: {id: "carrierIntegration.create.success"})
            );
          props.onSuccess();
          props.onClose();
        })
        .catch((error) => {
          handleError(error);
        })
        .finally(() => {
          if (isMounted.current) {
            setIsLoading(false);
          }
        });
    }
  };

  const carrierDropdownOptions: KeyValuePair<string, string>[] = carrierOptions.map((carrier) => ({
    key: carrier.name,
    value: carrier.carrierIntegrationsId.toString()
  }));

  const modalTitle = props.isEditMode
    ? intl.formatMessage({ id: "carrierIntegration.edit" })
    : intl.formatMessage({ id: "carrierIntegration.create.title" });

  const saveButtonText = props.isEditMode
    ? intl.formatMessage({ id: "carrierIntegration.update" })
    : intl.formatMessage({ id: "carrierIntegration.save" });

  return (
    <Modal isOpen={true} className="modal-dialog-centered modal-md">
      {showLoadingIndicator ? (
         <LoadingIndicator white />
      ) : (
      <>
        <div className="modal-header pb-0">
          <h6 className="modal-title">{modalTitle}</h6>
          <button type="button" className="close" onClick={props.onClose}>
            <span>&times;</span>
          </button>
        </div>

        <div className="modal-body">
          <Form className="carrier-form">
            <TextFormInput
              labelTranslationId="carrierIntegration.spid"
              placeholderTranslationId="carrierIntegration.spid.placeholder"
              required
              value={formData.spid}
              handleInputChange={(value) => handleInputChange("spid", value)}
              errorHandler={getErrorHandler(nameOf<CreateIntegrationMappingDto>("spid"))}
            />

            <DropdownFormInput
              labelTranslationId="carrierIntegration.losingcarrier"
              required
              value={formData.losingCarrier ? formData.losingCarrier.toString() : undefined}
              handleInputChange={(value) =>
                handleInputChange("losingCarrier", value ? Number(value) : 0)
              }
              options={carrierDropdownOptions}
              disabled={isLoadingCarriers}
              placeHolder="select"
              errorHandler={getErrorHandler(nameOf<CreateIntegrationMappingDto>("losingCarrier"))}
            />

            <TextFormInput
              labelTranslationId="carrierIntegration.losingcarrierspid"
              placeholderTranslationId="carrierIntegration.losingcarrierspid.placeholder"
              required
              value={formData.losingCarrierSpid}
              handleInputChange={(value) => handleInputChange("losingCarrierSpid", value)}
              errorHandler={getErrorHandler(nameOf<CreateIntegrationMappingDto>("losingCarrierSpid"))}
            />

            <TextFormInput
              labelTranslationId="carrierIntegration.losingcarrieraccountID"
              placeholderTranslationId="carrierIntegration.losingcarrieraccountID.placeholder"
              required
              value={formData.carrierAccountId}
              handleInputChange={(value) => handleInputChange("carrierAccountId", value)}
              errorHandler={getErrorHandler(nameOf<CreateIntegrationMappingDto>("carrierAccountId"))}
            />

            <FormGroup check className="mt-2">
              <Label check>
                <Input
                  type="checkbox"
                  checked={formData.integrationEnabled}
                  onChange={(e) => handleInputChange("integrationEnabled", e.target.checked)}
                />
                <FormattedMessage id="integration.enabled" defaultMessage="Integration Enabled" />
              </Label>
            </FormGroup>
          </Form>
        </div>

        <div className="carrier-buttons mr-4 mb-4">
          <Button color="secondary lblue-button-border " onClick={props.onClose}>
            <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            color="primary"
            className={`ml-auto ${theme === "light" ? "bg-lblue no-border" : ""}`}
            onClick={handleSubmit}
            disabled={isLoading}>
            {isLoading && <i className="fas fa-spinner fa-spin mr-2" />}
            {saveButtonText}
          </Button>
        </div>
      </>
      )}
    </Modal>
  );
}

const validateModel = (model: CreateIntegrationMappingDto) => {
  const errors: Errors = {};

  if (!model.spid || model.spid.length === 0) {
    AddError(errors, nameOf<CreateIntegrationMappingDto>("spid"), "carrierIntegration.spid.required");
  }
  if (!model.losingCarrier) {
    AddError(errors, nameOf<CreateIntegrationMappingDto>("losingCarrier"), "carrierIntegration.losingcarrier.required");
  }
  if (!model.losingCarrierSpid) {
    AddError(errors, nameOf<CreateIntegrationMappingDto>("losingCarrierSpid"), "carrierIntegration.losingcarrierspid.required");
  }
  if (!model.carrierAccountId) {
    AddError(errors, nameOf<CreateIntegrationMappingDto>("carrierAccountId"), "carrierIntegration.accountid.required");
  }

  return errors;
};